import React from 'react';
import PropTypes from 'prop-types';
import {
  IconButton,
  InputAdornment,
  Popover,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AddLinkIcon from '@mui/icons-material/AddLink';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import MicIcon from '@mui/icons-material/Mic';
import LinkIcon from '@mui/icons-material/Link';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import ActionMenuItem from 'src/views/management/ChatView/NewMessage/ActionMenuItem';
import { ReactComponent as DynamicFieldsIcon } from 'src/assets/images/dynamic-fields-icon.svg';
import { STYLE_OPTIONS } from 'src/constants';
import { contactCardMenuLabel } from 'src/helpers/contactCardAttachments';

const inputAdornmentStyle = {
  padding: 0,
  color: '#B5B5B5',
  background: '#EEEEEE',
  '&:hover svg': STYLE_OPTIONS.SVG_COLOR_BLUE,
};

function InputStartAdornment({
  handleOpenPopover,
  handleClosePopover,
  popoverAnchorEl,
  isLoading,
  isAdditionalActionsDisabled,
  isScheduleDisabled,
  openUploadDialog,
  openMessageTemplateDialog,
  openDynamicFieldsDialog,
  openSmartLinksDialog,
  openSmartFormsDialog,
  openCpPageDialog,
  openScheduleDialog,
  onAttachContactCard,
  onRecordVoiceMemo,
  canRecordVoiceMemo,
  textingProvider,
  hasAttachment,
  isContactCardAttached,
  canAddAnotherFile,
}) {
  // Hide contact card option for PB users or if contact card is already attached
  const showContactCardOption = textingProvider !== 'PB' && !isContactCardAttached;

  // Determine labels based on attachment state. CON-1925 — in the
  // multi-attachment beta, while there is room for one more file the item
  // ADDS instead of replacing and says so.
  const fileLabel = hasAttachment
    ? (canAddAnotherFile ? 'Add Another File' : 'Replace with File')
    : 'Attach File';
  // CON-1934 — the same room ADDS the card in front and keeps every file;
  // full, it replaces the first file only.
  const contactCardLabel = contactCardMenuLabel({ hasAttachment, canAdd: !!canAddAnotherFile });
  return (
    <>
      <InputAdornment
        position="start"
        sx={{
          margin: 'auto 8px 11px 0px',
        }}
      >
        <IconButton
          size="small"
          onClick={handleOpenPopover}
          disabled={isLoading || isAdditionalActionsDisabled}
          sx={{
            ...inputAdornmentStyle,
          }}
        >
          <AddIcon
            sx={{
              color: popoverAnchorEl ? STYLE_OPTIONS.SVG_COLOR_BLUE : '#B5B5B5',
            }}
          />
        </IconButton>
      </InputAdornment>
      <Popover
        open={!!popoverAnchorEl}
        anchorEl={popoverAnchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        slotProps={{
          root: {
            sx: {
              '& .MuiMenuItem-root': {
                padding: '8px 6px',
                borderRadius: '4px',
                marginBottom: '4px',
                '&:last-child': { marginBottom: 0 },
              }
            }
          },
          paper: {
            sx: {
              borderRadius: '12px',
              padding: '8px'
            }
          }
        }}
      >
        {openUploadDialog && (
          <ActionMenuItem
            disabled={isLoading}
            onClick={openUploadDialog}
            icon={<AttachFileIcon fontSize="small" />}
            label={fileLabel}
          />
        )}
        {showContactCardOption && onAttachContactCard && (
          <ActionMenuItem
            disabled={isLoading}
            onClick={onAttachContactCard}
            icon={<ContactPageIcon fontSize="small" />}
            label={contactCardLabel}
          />
        )}
        {/* CON-1674 — hidden entirely when the browser cannot record, rather
            than shown disabled. A menu item that never works is the failure
            we removed from the audio player; it should not reappear here. */}
        {canRecordVoiceMemo && onRecordVoiceMemo && (
          <ActionMenuItem
            disabled={isLoading}
            onClick={onRecordVoiceMemo}
            icon={<MicIcon fontSize="small" />}
            label={hasAttachment ? 'Replace with Voice Memo' : 'Record a Voice Memo'}
          />
        )}
        {openMessageTemplateDialog && (
          <ActionMenuItem
            disabled={isLoading}
            onClick={openMessageTemplateDialog}
            icon={<MessageOutlinedIcon fontSize="small" />}
            label="Message template"
          />
        )}
        {openDynamicFieldsDialog && (
          <ActionMenuItem
            disabled={isLoading}
            onClick={openDynamicFieldsDialog}
            icon={<DynamicFieldsIcon fontSize="small" />}
            label="Insert Dynamic Field"
          />
        )}
        {openSmartLinksDialog && (
          <ActionMenuItem
            disabled={isLoading}
            onClick={openSmartLinksDialog}
            icon={<LinkIcon fontSize="small" />}
            label="Insert Smart Link"
          />
        )}
        {openSmartFormsDialog && (
          <ActionMenuItem
            disabled={isLoading}
            onClick={openSmartFormsDialog}
            icon={<AddLinkIcon fontSize="small" />}
            label="Insert Smart Form URL"
          />
        )}
        {openCpPageDialog && (
          <ActionMenuItem
            disabled={isLoading}
            onClick={openCpPageDialog}
            icon={<AddLinkIcon fontSize="small" />}
            label="Insert Connect Page URL"
          />
        )}
        {openScheduleDialog && (
          <ActionMenuItem
            disabled={isScheduleDisabled || isLoading}
            onClick={openScheduleDialog}
            icon={<ScheduleSendIcon fontSize="small" />}
            label="Schedule a message"
          />
        )}
      </Popover>
    </>
  );
}

InputStartAdornment.propTypes = {
  onRecordVoiceMemo: PropTypes.func,
  canRecordVoiceMemo: PropTypes.bool,
  handleOpenPopover: PropTypes.func.isRequired,
  handleClosePopover: PropTypes.func.isRequired,
  popoverAnchorEl: PropTypes.oneOfType([
    PropTypes.oneOf([null]),
    PropTypes.object,
  ]),
  isLoading: PropTypes.bool,
  isAdditionalActionsDisabled: PropTypes.bool,
  isScheduleDisabled: PropTypes.bool,
  openUploadDialog: PropTypes.func,
  openMessageTemplateDialog: PropTypes.func,
  openDynamicFieldsDialog: PropTypes.func,
  openSmartLinksDialog: PropTypes.func,
  openSmartFormsDialog: PropTypes.func,
  openCpPageDialog: PropTypes.func,
  openScheduleDialog: PropTypes.func,
  onAttachContactCard: PropTypes.func,
  textingProvider: PropTypes.string,
  hasAttachment: PropTypes.bool,
  isContactCardAttached: PropTypes.bool,
  canAddAnotherFile: PropTypes.bool,
};

InputStartAdornment.defaultProps = {
  isLoading: false,
  isAdditionalActionsDisabled: false,
  isScheduleDisabled: false,
  textingProvider: null,
  hasAttachment: false,
  isContactCardAttached: false,
  canAddAnotherFile: false,
};

export default InputStartAdornment;
